import React, { useState, useEffect } from 'react';
import { X, Heart, Share2, ShoppingBag, ChevronLeft, ChevronRight, Camera, Quote } from 'lucide-react';
import api from '../utils/api';

// Shoppable Hotspot component
const ProductHotspot = ({ hotspot, onClick }) => {
  if (!hotspot?.enabled) return null;
  
  return (
    <button
      onClick={onClick}
      className="absolute w-8 h-8 bg-yellow-400 border-4 border-black rounded-full flex items-center justify-center shadow-[2px_2px_0px_black] hover:scale-110 transition-transform animate-pulse"
      style={{
        left: `${hotspot.x}%`,
        top: `${hotspot.y}%`,
        transform: 'translate(-50%, -50%)'
      }}
    >
      <ShoppingBag size={14} className="text-black" />
    </button>
  );
};

// Spread Layout Components
const FullBleedSpread = ({ spread, onHotspotClick }) => (
  <div className="relative w-full h-full">
    <img 
      src={spread.images[0]?.url} 
      alt={spread.caption || 'Lookbook spread'}
      className="w-full h-full object-cover"
    />
    <ProductHotspot 
      hotspot={spread.images[0]?.hotspot} 
      onClick={() => onHotspotClick(spread.images[0]?.hotspot?.product)}
    />
    {spread.caption && (
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
        <p className="text-white font-mono text-sm">{spread.caption}</p>
      </div>
    )}
  </div>
);

const SplitSpread = ({ spread, onHotspotClick }) => (
  <div className="grid grid-cols-2 h-full gap-1">
    {spread.images.slice(0, 2).map((img, idx) => (
      <div key={idx} className="relative">
        <img 
          src={img.url} 
          alt={spread.caption || 'Lookbook image'}
          className="w-full h-full object-cover"
        />
        <ProductHotspot 
          hotspot={img.hotspot} 
          onClick={() => onHotspotClick(img.hotspot?.product)}
        />
      </div>
    ))}
  </div>
);

const GallerySpread = ({ spread, onHotspotClick }) => (
  <div className="grid grid-cols-3 gap-2 h-full p-4">
    {spread.images.map((img, idx) => (
      <div key={idx} className="relative overflow-hidden">
        <img 
          src={img.url} 
          alt={spread.caption || 'Lookbook image'}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
        />
        <ProductHotspot 
          hotspot={img.hotspot} 
          onClick={() => onHotspotClick(img.hotspot?.product)}
        />
      </div>
    ))}
  </div>
);

const Lookbook = ({ lookbook, onClose, onProductClick, onLike }) => {
  const [currentSpread, setCurrentSpread] = useState(0);
  const [liked, setLiked] = useState(false);
  const [showProductModal, setShowProductModal] = useState(null);
  const [showBTS, setShowBTS] = useState(false);

  if (!lookbook) return null;

  const handleNext = () => {
    setCurrentSpread(prev => Math.min(prev + 1, lookbook.spreads.length - 1));
  };

  const handlePrev = () => {
    setCurrentSpread(prev => Math.max(prev - 1, 0));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowRight') handleNext();
    if (e.key === 'ArrowLeft') handlePrev();
    if (e.key === 'Escape') onClose();
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleHotspotClick = (product) => {
    if (product) {
      setShowProductModal(product);
    }
  };

  const handleLike = async () => {
    if (!liked && onLike) {
      await onLike(lookbook._id);
    }
    setLiked(true);
  };

  const renderSpread = () => {
    const spread = lookbook.spreads[currentSpread];
    if (!spread) return null;

    switch (spread.layout) {
      case 'split':
        return <SplitSpread spread={spread} onHotspotClick={handleHotspotClick} />;
      case 'gallery':
        return <GallerySpread spread={spread} onHotspotClick={handleHotspotClick} />;
      case 'full_bleed':
      default:
        return <FullBleedSpread spread={spread} onHotspotClick={handleHotspotClick} />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 md:px-8 py-4 bg-black border-b border-white/20">
        <div className="flex items-center gap-4">
          <button 
            onClick={onClose}
            className="text-white hover:text-yellow-400 transition-colors"
          >
            <X size={28} />
          </button>
          <div>
            <h2 className="text-white font-black text-lg md:text-xl uppercase tracking-tighter">
              {lookbook.title}
            </h2>
            <p className="text-white/60 text-xs md:text-sm font-mono">
              {lookbook.season} • {currentSpread + 1} / {lookbook.spreads.length}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {lookbook.btsContent?.length > 0 && (
            <button
              onClick={() => setShowBTS(true)}
              className="text-white/60 hover:text-white transition-colors flex items-center gap-2 text-sm"
            >
              <Camera size={18} />
              <span className="hidden md:inline">BTS</span>
            </button>
          )}
          
          <button 
            onClick={handleLike}
            className={`flex items-center gap-2 px-3 py-2 border-2 transition-all ${
              liked 
                ? 'bg-red-600 border-red-600 text-white' 
                : 'border-white/40 text-white/60 hover:border-white hover:text-white'
            }`}
          >
            <Heart size={18} fill={liked ? 'currentColor' : 'none'} />
            <span className="text-sm font-bold">{lookbook.likes + (liked ? 1 : 0)}</span>
          </button>
          
          <button className="text-white/60 hover:text-white transition-colors">
            <Share2 size={20} />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-grow flex relative overflow-hidden">
        {/* Navigation Arrows */}
        {currentSpread > 0 && (
          <button
            onClick={handlePrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 text-white p-3 hover:bg-yellow-400 hover:text-black transition-colors"
          >
            <ChevronLeft size={32} />
          </button>
        )}
        
        {currentSpread < lookbook.spreads.length - 1 && (
          <button
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 text-white p-3 hover:bg-yellow-400 hover:text-black transition-colors"
          >
            <ChevronRight size={32} />
          </button>
        )}

        {/* Spread */}
        <div className="flex-grow relative">
          {renderSpread()}
        </div>

        {/* Sidebar Info */}
        <div className="hidden lg:block w-80 bg-black border-l border-white/20 p-6 overflow-y-auto">
          {/* Story Section */}
          {lookbook.story && (
            <div className="mb-8">
              {lookbook.story.title && (
                <h3 className="text-yellow-400 font-black text-lg uppercase mb-3">
                  {lookbook.story.title}
                </h3>
              )}
              {lookbook.story.content && (
                <p className="text-white/80 font-mono text-sm leading-relaxed mb-4">
                  {lookbook.story.content}
                </p>
              )}
              {lookbook.story.quote && (
                <div className="border-l-4 border-yellow-400 pl-4 py-2">
                  <Quote size={16} className="text-yellow-400 mb-2" />
                  <p className="text-white italic">"{lookbook.story.quote}"</p>
                  {lookbook.story.quoteAuthor && (
                    <p className="text-white/50 text-sm mt-2">— {lookbook.story.quoteAuthor}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Credits */}
          {lookbook.credits && (
            <div className="mb-8">
              <h4 className="text-white/40 text-xs uppercase tracking-widest mb-3">Credits</h4>
              <div className="space-y-1 text-sm">
                {lookbook.credits.photographer && (
                  <p className="text-white/70">
                    <span className="text-white/40">Photography:</span> {lookbook.credits.photographer}
                  </p>
                )}
                {lookbook.credits.stylist && (
                  <p className="text-white/70">
                    <span className="text-white/40">Styling:</span> {lookbook.credits.stylist}
                  </p>
                )}
                {lookbook.credits.creativeDirector && (
                  <p className="text-white/70">
                    <span className="text-white/40">Creative Director:</span> {lookbook.credits.creativeDirector}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Models */}
          {lookbook.models?.length > 0 && (
            <div className="mb-8">
              <h4 className="text-white/40 text-xs uppercase tracking-widest mb-3">Models</h4>
              <div className="space-y-3">
                {lookbook.models.map((model, idx) => (
                  <div key={idx} className="text-sm">
                    <p className="text-white font-bold">{model.name}</p>
                    {model.height && (
                      <p className="text-white/50 text-xs">{model.height}</p>
                    )}
                    {model.sizesWorn?.length > 0 && (
                      <p className="text-yellow-400 text-xs mt-1">
                        Wearing: {model.sizesWorn.map(s => s.size).join(', ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Shop Featured */}
          {lookbook.isShoppable && lookbook.featuredProducts?.length > 0 && (
            <div>
              <h4 className="text-white/40 text-xs uppercase tracking-widest mb-3">Shop This Look</h4>
              <div className="space-y-3">
                {lookbook.featuredProducts.slice(0, 3).map((product) => (
                  <button
                    key={product._id}
                    onClick={() => onProductClick?.(product)}
                    className="w-full flex items-center gap-3 p-2 border border-white/20 hover:border-yellow-400 transition-colors group"
                  >
                    <img 
                      src={product.images?.[0]} 
                      alt={product.name}
                      className="w-12 h-12 object-cover"
                    />
                    <div className="text-left flex-grow">
                      <p className="text-white text-sm font-bold group-hover:text-yellow-400 transition-colors">
                        {product.name}
                      </p>
                      <p className="text-white/50 text-xs">₹{product.price}</p>
                    </div>
                    <ShoppingBag size={16} className="text-white/40 group-hover:text-yellow-400" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-1 bg-white/20">
        <div 
          className="h-full bg-yellow-400 transition-all duration-300"
          style={{ width: `${((currentSpread + 1) / lookbook.spreads.length) * 100}%` }}
        />
      </div>

      {/* BTS Modal */}
      {showBTS && (
        <div className="absolute inset-0 bg-black/95 z-30 flex items-center justify-center p-8">
          <button
            onClick={() => setShowBTS(false)}
            className="absolute top-4 right-4 text-white hover:text-yellow-400"
          >
            <X size={28} />
          </button>
          <div className="max-w-4xl w-full">
            <h3 className="text-white font-black text-2xl uppercase mb-6">Behind The Scenes</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {lookbook.btsContent?.map((item, idx) => (
                <div key={idx} className="relative">
                  {item.type === 'video' ? (
                    <video 
                      src={item.url} 
                      controls 
                      className="w-full aspect-square object-cover"
                    />
                  ) : (
                    <img 
                      src={item.url} 
                      alt={item.caption || 'BTS'}
                      className="w-full aspect-square object-cover"
                    />
                  )}
                  {item.caption && (
                    <p className="text-white/60 text-xs mt-1">{item.caption}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Product Quick View Modal */}
      {showProductModal && (
        <div className="absolute inset-0 bg-black/90 z-40 flex items-center justify-center p-4">
          <div className="bg-white max-w-md w-full border-4 border-black shadow-[8px_8px_0px_black] p-6 relative">
            <button
              onClick={() => setShowProductModal(null)}
              className="absolute top-2 right-2 text-black hover:text-red-600"
            >
              <X size={24} />
            </button>
            <img 
              src={showProductModal.images?.[0]} 
              alt={showProductModal.name}
              className="w-full aspect-square object-cover border-2 border-black mb-4"
            />
            <h3 className="font-black text-xl uppercase mb-2">{showProductModal.name}</h3>
            <p className="text-2xl font-black mb-4">₹{showProductModal.price}</p>
            <button
              onClick={() => onProductClick?.(showProductModal)}
              className="w-full bg-yellow-400 text-black py-3 font-black uppercase tracking-wider border-2 border-black hover:shadow-[4px_4px_0px_black] transition-shadow"
            >
              View Product
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Lookbook;
